import { gsap } from 'gsap';

/** System bar target widths as % of `.xiaoos-sys-blocks` (matches 30/140/180 of 220px design) */
const SYS_BAR = {
  b1: '13.636363%',
  b2: '63.636363%',
  b3: '81.818181%',
} as const;

/**
 * Terminal Startup Animation Component
 * xiaoOS style loading sequence
 */

export interface StartupAnimationOptions {
  /**
   * Called once the intro has finished and its DOM has been removed.
   * Replaces the legacy hard-navigation to `/home` so the overlay can
   * dissolve on top of an already-rendered page.
   * `skipped` is true when the visitor bailed out via the bypass control —
   * callers should skip any follow-up cinematics too.
   */
  onFinish?: (skipped?: boolean) => void;
  /**
   * Boot beats, in order: 0 X-grid, 1 strike bars, 2 diamond, 3 shapes,
   * 4 System Loading, 5 fast bar, 6 geometric reveal. Zero-duration timeline
   * callbacks, so they follow timeScale and skip; never called under reduced motion.
   */
  onStage?: (stage: number) => void;
}

/** Phones and coarse pointers take the shortened boot. */
export const quickBoot = (): boolean => !window.matchMedia('(min-width: 900px) and (pointer: fine)').matches;

export class StartupAnimation {
  private container: HTMLElement | null = null;
  private isAnimating = false;
  private tl: gsap.core.Timeline | null = null;
  private skipped = false;
  private finishing = false;
  private readonly reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  private readonly onEscape = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.skip();
  };
  private readonly options: StartupAnimationOptions;

  constructor(options: StartupAnimationOptions = {}) {
    this.options = options;
    this.init();
  }

  private init(): void {
    this.createAnimationContainer();
    this.startAnimation();
  }

  private createAnimationContainer(): void {
    const markup = `
      <div class="xiaoos-display-area" id="xiaoos-display-area">
          
          <!-- 1. The X Matrix Grid -->
          <div class="xiaoos-x-grid" id="xiaoos-x-grid"></div>

          <!-- 2. Strikeout bars -->
          <div class="xiaoos-strike-bars" id="xiaoos-strike-bars">
              <div class="xiaoos-strike-bar"></div>
              <div class="xiaoos-strike-bar"></div>
              <div class="xiaoos-strike-bar"></div>
          </div>

          <!-- Initial Transition Diamond (Behind bars) -->
          <div class="xiaoos-transition-diamond" id="xiaoos-transition-diamond">
              <div class="xiaoos-diamond-shape" id="xiaoos-diamond-shape"></div>
              <div class="xiaoos-diamond-x-lines" id="xiaoos-diamond-x-lines">
                  <div class="xiaoos-x-line xiaoos-x-line-1"></div>
                  <div class="xiaoos-x-line xiaoos-x-line-2"></div>
              </div>
          </div>

          <!-- 3. Concentric Geometric Shapes -->
          <div class="xiaoos-shape-container" id="xiaoos-shape-container">
              
              <!-- Shape 1: Just Diamond -->
              <svg class="xiaoos-shape-1" viewBox="0 0 150 150">
                  <polygon points="75,10 140,75 75,140 10,75" class="xiaoos-shape-path" />
              </svg>

              <!-- Shape 2: Diamond + Triangle intersecting bottom -->
              <svg class="xiaoos-shape-2" viewBox="0 0 150 150">
                  <polygon points="75,10 140,75 75,140 10,75" class="xiaoos-shape-path" />
                  <polygon points="10,50 140,50 75,140" class="xiaoos-shape-path" />
              </svg>

              <!-- Shape 3: Diamond + Circle inside -->
              <svg class="xiaoos-shape-3" viewBox="0 0 150 150">
                  <polygon points="75,10 140,75 75,140 10,75" class="xiaoos-shape-path" />
                  <circle cx="75" cy="75" r="45" class="xiaoos-shape-path" />
              </svg>

              <!-- Shape 4: Circle + Previous Triangle -->
              <svg class="xiaoos-shape-4" viewBox="0 0 150 150">
                  <circle cx="75" cy="75" r="45" class="xiaoos-shape-path" />
                  <polygon points="10,50 140,50 75,140" class="xiaoos-shape-path" />
              </svg>

              <!-- Shape 5: Circle + Triangle + Inner Triangle -->
              <svg class="xiaoos-shape-5" viewBox="0 0 150 150">
                  <circle cx="75" cy="75" r="45" class="xiaoos-shape-path" />
                  <polygon points="10,50 140,50 75,140" class="xiaoos-shape-path" />
                  <polygon points="75,50 105,95 45,95" class="xiaoos-shape-path" />
              </svg>
          </div>

          <!-- 4. System Loading -->
          <div class="xiaoos-sys-container">
              <div class="xiaoos-sys-blocks" id="xiaoos-sys-blocks">
                  <div class="xiaoos-sys-b1"></div>
                  <div class="xiaoos-sys-b2"></div>
                  
                  <div class="xiaoos-sys-text-container">
                      <div class="xiaoos-sys-text" id="xiaoos-sys-text">System Loading</div>
                      <div class="xiaoos-sys-fast-bar-container" id="xiaoos-sys-fast-bar-container">
                          <div class="xiaoos-sys-fast-bar" id="xiaoos-sys-fast-bar"></div>
                      </div>
                  </div>

                  <div class="xiaoos-sys-b3"></div>
              </div>
          </div>

      </div>

      <!-- Progress Bar & Hint Text -->
      <div class="xiaoos-bottom-ui" id="xiaoos-bottom-ui">
          <div class="xiaoos-loading-text">Loading...</div>
          <div class="xiaoos-progress-bar-bg">
              <div class="xiaoos-progress-bar-fill" id="xiaoos-progress-bar"></div>
          </div>
          <div class="xiaoos-hint-text">
              Opening David Xiao’s file. Profile, selected work, experience and contact.
          </div>
      </div>

      <div class="xiaoos-reveal-outline" id="xiaoos-reveal-outline"></div>
      <div class="xiaoos-reveal-solid" id="xiaoos-reveal-solid"></div>

      <button type="button" class="xiaoos-skip-btn" id="xiaoos-skip-btn">Bypass ▸</button>
    `;

    // Reuse the #startup-animation placeholder in base.html if it exists to avoid duplicate IDs.
    let overlay = document.getElementById('startup-animation') as HTMLElement | null;
    if (overlay) {
      overlay.className = 'xiaoos-loader-container';
      overlay.style.opacity = '';
      overlay.style.transition = '';
      overlay.innerHTML = markup;
    } else {
      overlay = document.createElement('div');
      overlay.id = 'startup-animation';
      overlay.className = 'xiaoos-loader-container';
      overlay.innerHTML = markup;
      document.body.appendChild(overlay);
    }
    this.container = overlay;
    overlay.removeAttribute('aria-hidden');
    overlay.querySelector('.xiaoos-display-area')?.setAttribute('aria-hidden', 'true');
    // The coordinator's server-rendered link spans every intro phase.
    if (document.getElementById('xw-intro-bypass')) {
      overlay.querySelector<HTMLElement>('#xiaoos-skip-btn')!.hidden = true;
    }

    // Skip control — the intro is a bounded courtesy, never a gate.
    const skipBtn = overlay.querySelector<HTMLButtonElement>('#xiaoos-skip-btn');
    skipBtn?.addEventListener('click', () => this.skip());
    document.addEventListener('keydown', this.onEscape);

    // Generate the 3x10 grid of X's
    const grid = document.getElementById('xiaoos-x-grid');
    if (grid) {
        for (let r = 0; r < 3; r++) {
            for (let c = 0; c < 10; c++) {
                const span = document.createElement('span');
                span.className = 'xiaoos-x-item';
                span.innerText = 'X';
                grid.appendChild(span);
            }
        }
    }
  }

  private startAnimation(): void {
    if (this.isAnimating) return;
    this.isAnimating = true;
    this.skipped = false;
    this.finishing = false;

    if (this.reducedMotion) {
      // Keep the identity and narrative, not flashes, zooms or moving geometry.
      this.tl = gsap.timeline({ onComplete: () => this.redirectToHome() })
        .set('.xiaoos-x-item', { opacity: 1 })
        .set('#xiaoos-bottom-ui', { opacity: 1 })
        .set('#xiaoos-x-grid', { opacity: 0 }, 0.4)
        .set('.xiaoos-strike-bar', { width: '100%' }, 0.4)
        .set('#xiaoos-strike-bars', { opacity: 0 }, 0.7)
        .set('.xiaoos-shape-1', { opacity: 1 }, 0.7)
        .set('.xiaoos-shape-1', { opacity: 0 }, 1.05)
        .set('#xiaoos-sys-blocks', { opacity: 1 }, 1.05)
        .to({}, { duration: 0.45 });
      return;
    }

    // Resets
    gsap.set("#xiaoos-display-area", { display: "flex" });
    gsap.set("#xiaoos-reveal-outline", { scale: 0, opacity: 1 });
    gsap.set("#xiaoos-reveal-solid", { scale: 0, opacity: 1 });
    
    gsap.set(".xiaoos-x-item", { opacity: 0 });
    gsap.set(".xiaoos-strike-bar", { width: "100%", scaleX: 0, transformOrigin: 'left center' });
    gsap.set("#xiaoos-strike-bars", { opacity: 1 });
    gsap.set("#xiaoos-transition-diamond", { opacity: 0 });
    gsap.set("#xiaoos-diamond-shape", { scale: 1, opacity: 1 });
    gsap.set("#xiaoos-diamond-x-lines", { opacity: 1 });
    gsap.set(".xiaoos-shape-1, .xiaoos-shape-2, .xiaoos-shape-3, .xiaoos-shape-4, .xiaoos-shape-5", { opacity: 0, scale: 1 });
    
    gsap.set("#xiaoos-sys-blocks", { opacity: 0 });
    gsap.set(".xiaoos-sys-b1", { opacity: 1 });
    gsap.set(".xiaoos-sys-b2", { width: 0, opacity: 1 });
    gsap.set(".xiaoos-sys-b3", { width: 0, opacity: 1 });
    
    gsap.set("#xiaoos-sys-fast-bar-container", { opacity: 0 });
    gsap.set("#xiaoos-sys-fast-bar", { width: "85%" });
    
    gsap.set("#xiaoos-bottom-ui", { opacity: 0 });
    gsap.set("#xiaoos-progress-bar", { width: "100%", scaleX: 0, transformOrigin: 'left center' });

    const tl = gsap.timeline();
    this.tl = tl;
    const stage = (n: number) => () => this.options.onStage?.(n);

    // Timing scale ~0.5× vs original — same beats, faster pass
    // 0. Bottom UI begins loading immediately
    tl.to("#xiaoos-bottom-ui", { opacity: 1, duration: 0.15 }, 0);

    // 1. Diagonal fill of X's
    tl.to(".xiaoos-x-item", {
        opacity: 1,
        duration: 0.04,
        stagger: {
            each: 0.028,
            grid: [3, 10],
            from: "start"
        }
    }, 0.12);
    tl.add(stage(0), 0.12);

    // 2. White Bars Strike Out the X's progressively
    tl.to(".xiaoos-strike-bar", {
        scaleX: 1,
        duration: 0.2,
        stagger: 0.05,
        ease: "power2.inOut"
    }, "+=0.12");
    tl.add(stage(1), "<");

    // 3. Diamond appears DURING the bars striking through
    tl.set(".xiaoos-x-item", { opacity: 0 }, "-=0.1")
      .to("#xiaoos-transition-diamond", { opacity: 1, duration: 0.06 }, "-=0.1");
    tl.add(stage(2), "<");

    // 4. Diamond X glitches (fewer repeats, snappier)
    tl.to("#xiaoos-diamond-x-lines", { opacity: 0, duration: 0.18 }, "+=0.04");

    // 5. Bars disappear just before the diamond expansion ends
    tl.to("#xiaoos-strike-bars", { opacity: 0, duration: 0.06 }, "-=0.14");

    // 6. X disappears fully as Diamond expands slightly
    tl.set("#xiaoos-diamond-x-lines", { opacity: 0 }, "-=0.1")
      .to("#xiaoos-diamond-shape", { scale: 1.1, duration: 0.22, ease: "power2.out" }, "-=0.1")
      .to("#xiaoos-transition-diamond", { opacity: 0, duration: 0.03 }, "+=0.08");

    // 7. Large X flash, then vanish
    tl.to("#xiaoos-diamond-x-lines", { opacity: 1, duration: 0 }, "+=0.04")
      .to("#xiaoos-diamond-shape", { opacity: 0, duration: 0 }, "<")
      .to("#xiaoos-transition-diamond", { opacity: 1, duration: 0.03 })
      .to("#xiaoos-transition-diamond", { opacity: 0, duration: 0.06 }, "+=0.06");

    tl.to({}, { duration: 0.12 });

    // 8. Concentric shape sequencing (tighter holds)
    tl.set(".xiaoos-shape-1", { opacity: 1 })
      .add(stage(3), "<")
      .set(".xiaoos-shape-1", { opacity: 0 }, "+=0.12")
      .set(".xiaoos-shape-2", { opacity: 1 }, "+=0.04")
      .set(".xiaoos-shape-2", { opacity: 0 }, "+=0.12")
      .set(".xiaoos-shape-3", { opacity: 1 }, "+=0.04")
      .set(".xiaoos-shape-3", { opacity: 0 }, "+=0.12")
      .set(".xiaoos-shape-4", { opacity: 1 }, "+=0.04")
      .set(".xiaoos-shape-4", { opacity: 0 }, "+=0.12")
      .set(".xiaoos-shape-5", { opacity: 1 }, "+=0.04")
      .set(".xiaoos-shape-5", { opacity: 0 }, "+=0.12");

    tl.to({}, { duration: 0.14 });

    // 9. System Loading (% widths scale with narrow `.xiaoos-sys-container` on phones)
    tl.set(".xiaoos-sys-b1", { width: SYS_BAR.b1 })
      .set(".xiaoos-sys-b2", { width: SYS_BAR.b2, opacity: 0 })
      .set(".xiaoos-sys-b3", { width: SYS_BAR.b3, opacity: 0 })
      .to("#xiaoos-sys-blocks", { opacity: 1, duration: 0.05 })
      .add(stage(4), "<");

    tl.to(".xiaoos-sys-b2, .xiaoos-sys-b3", { opacity: 1, duration: 0.03 }, "+=0.05")
      .to(".xiaoos-sys-b2, .xiaoos-sys-b3", { opacity: 0, duration: 0.03 })
      .to(".xiaoos-sys-b2, .xiaoos-sys-b3", { opacity: 1, duration: 0.03 });

    tl.set(".xiaoos-sys-b2, .xiaoos-sys-b3", { width: 0 })
      .to(".xiaoos-sys-b2", { width: SYS_BAR.b2, duration: 0.55, ease: "power1.inOut" }, "+=0.04")
      .to(".xiaoos-sys-b3", { width: SYS_BAR.b3, duration: 0.55, ease: "power1.inOut" }, "-=0.55")
      // Login beat, inside the bars' existing growth: no time is added.
      .add(() => {
        const text = document.getElementById("xiaoos-sys-text");
        if (text) text.textContent = "Login: guest";
      }, "-=0.28");

    tl.to(".xiaoos-sys-text", { opacity: 0, duration: 0.12 })
      .to("#xiaoos-sys-fast-bar-container", { opacity: 1, duration: 0.12 }, "<")
      .set(".xiaoos-sys-text", { visibility: "hidden" });

    tl.to("#xiaoos-sys-fast-bar", { width: "100%", duration: 0.38, ease: "power3.inOut" })
      .add(stage(5), "<")
      .to("#xiaoos-sys-fast-bar-container", { opacity: 0, duration: 0.1 }, "+=0.06");

    tl.to(".xiaoos-sys-b1, .xiaoos-sys-b2, .xiaoos-sys-b3", { opacity: 0, duration: 0.1 }, "-=0.1");

    const totalDuration = tl.duration();
    tl.to("#xiaoos-progress-bar", { scaleX: 1, duration: totalDuration, ease: "power1.inOut" }, 0);

    tl.to("#xiaoos-bottom-ui", { opacity: 0, duration: 0.14 })
      .to("#xiaoos-display-area", { display: "none" }, "<");

    // 12. Geometric reveal into home
    tl.to("#xiaoos-reveal-outline", { scale: 1, duration: 0.42, ease: "power3.inOut" })
      .add(stage(6), "<")
      .to("#xiaoos-reveal-solid", { scale: 1, duration: 0.32, ease: "power3.in" }, "-=0.32")
      .to("#xiaoos-reveal-outline", { opacity: 0, duration: 0 }, "<")
      .to("#xiaoos-reveal-solid", { opacity: 0, duration: 0.42, ease: "power2.out" }, "+=0.04")
      .add(() => {
          this.redirectToHome();
      });
    // Same visual beats, budgeted to leave room for the City in an 8s arrival;
    // phones get the quick boot, the same beats at a faster clock.
    tl.timeScale(quickBoot() ? 2.9 : 1.85);
  }

  /** Bail out of the intro immediately (bypass button / Escape). */
  public skip(): void {
    if (!this.container || this.skipped) return;
    this.skipped = true;
    this.tl?.kill();
    this.redirectToHome();
  }

  private redirectToHome(): void {
    if (!this.container || this.finishing) return;
    this.finishing = true;

    // Fade out overlay
    this.container.style.opacity = '0';
    this.container.style.transition = this.reducedMotion ? 'none' : 'opacity 0.12s ease-out';

    setTimeout(() => {
      if (this.container && this.container.parentNode) {
        this.container.parentNode.removeChild(this.container);
      }
      this.container = null;
      this.isAnimating = false;
      this.tl = null;
      document.removeEventListener('keydown', this.onEscape);
      this.options.onFinish?.(this.skipped);
    }, this.skipped || this.reducedMotion ? 0 : 120);
  }

  // Public method to manually start animation (useful for testing)
  public restart(): void {
    if (this.isAnimating) return;
    this.createAnimationContainer();
    this.startAnimation();
  }

  // Check if animation is currently running
  public get isRunning(): boolean {
    return this.isAnimating;
  }
}

/**
 * Convenience factory. Kept for backwards compatibility with older boot code.
 * Prefer mounting via `src/home/bootOverlay.ts` which handles session gating.
 */
export function initStartupAnimation(options: StartupAnimationOptions = {}): StartupAnimation | null {
  try {
    return new StartupAnimation(options);
  } catch (error) {
    console.error('StartupAnimation: Error:', error);
    return null;
  }
}
